import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  AutoAwesome,
  CheckCircle,
  Email,
  Save,
} from '@mui/icons-material';
import {
  finalizeVisitorInspectionReport,
  generateVisitorInspectionReport,
  getVisitorInspectionReport,
  sendVisitorInspectionReportEmail,
  suggestVisitorInspectionReportSummary,
  updateVisitorInspectionReport,
  type VisitorInspectionReport,
  type VisitorInspectionReportAttendee,
} from '../services/propertyService';

/** Matches backend contractRequestCount (flags + wants_contract intent). */
const attendeeRequestedContract = (attendee: VisitorInspectionReportAttendee): boolean =>
  !!attendee.attendeeRequestsContract ||
  !!attendee.contractRequested ||
  attendee.followUpIntent === 'wants_contract';

const formatDate = (value?: string) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not recorded' : date.toLocaleDateString();
};

const toDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

const getDefaultReportPeriod = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  return {
    periodStart: toDateInputValue(start),
    periodEnd: toDateInputValue(end),
  };
};

const shiftReportPeriod = (periodStart: string, periodEnd: string, direction: -1 | 1) => {
  const start = new Date(periodStart);
  const end = new Date(periodEnd);
  start.setDate(start.getDate() + direction * 7);
  end.setDate(end.getDate() + direction * 7);
  return {
    periodStart: toDateInputValue(start),
    periodEnd: toDateInputValue(end),
  };
};

const VisitorInspectionReportEdit: React.FC = () => {
  const { agencySlug, propertyId } = useParams<{ agencySlug: string; propertyId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<VisitorInspectionReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggestingSummary, setSuggestingSummary] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [summary, setSummary] = useState('');
  const [agentNotes, setAgentNotes] = useState('');
  const [includedAttendeeIds, setIncludedAttendeeIds] = useState<string[]>([]);
  const [vendorVisibleContactDetails, setVendorVisibleContactDetails] = useState(false);
  const [vendorEmails, setVendorEmails] = useState('');
  const [periodStart, setPeriodStart] = useState(getDefaultReportPeriod().periodStart);
  const [periodEnd, setPeriodEnd] = useState(getDefaultReportPeriod().periodEnd);
  const [periodRefreshing, setPeriodRefreshing] = useState(false);
  const reportLoadedRef = useRef(false);
  const lastFetchedPeriodRef = useRef('');

  const ownerEmailList = useMemo(
    () => vendorEmails.split(',').map(email => email.trim()).filter(Boolean),
    [vendorEmails]
  );

  const loadReport = async () => {
    if (!agencySlug || !propertyId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getVisitorInspectionReport(agencySlug, propertyId);
      setReport(data);
      setSummary(data.summary || '');
      setAgentNotes(data.agentNotes || '');
      setIncludedAttendeeIds(data.includedAttendeeIds || data.attendees.map(attendee => attendee._id || '').filter(Boolean));
      setPeriodStart(data.periodStart || getDefaultReportPeriod().periodStart);
      setPeriodEnd(data.periodEnd || getDefaultReportPeriod().periodEnd);
      setVendorVisibleContactDetails(data.vendorVisibleContactDetails);
      setVendorEmails((data.vendorEmails || []).join(', '));
      lastFetchedPeriodRef.current = `${data.periodStart}|${data.periodEnd}`;
      reportLoadedRef.current = true;
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load visitor inspection report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [agencySlug, propertyId]);

  const hasValidPeriod = Boolean(periodStart && periodEnd && periodStart <= periodEnd);

  useEffect(() => {
    if (!agencySlug || !propertyId || !reportLoadedRef.current || !hasValidPeriod) return;

    const periodKey = `${periodStart}|${periodEnd}`;
    if (periodKey === lastFetchedPeriodRef.current) return;

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        setPeriodRefreshing(true);
        setError(null);
        const data = await getVisitorInspectionReport(agencySlug, propertyId, { periodStart, periodEnd });
        if (cancelled) return;
        setReport(data);
        setSummary(data.summary || '');
        setIncludedAttendeeIds(
          data.includedAttendeeIds?.length
            ? data.includedAttendeeIds
            : data.attendees.map(attendee => attendee._id || '').filter(Boolean)
        );
        lastFetchedPeriodRef.current = periodKey;
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load report for selected period');
        }
      } finally {
        if (!cancelled) setPeriodRefreshing(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [agencySlug, propertyId, periodStart, periodEnd, hasValidPeriod]);

  const persistDraft = async () => {
    if (!agencySlug || !propertyId) return null;
    const payload = { summary, agentNotes, includedAttendeeIds, periodStart, periodEnd, vendorVisibleContactDetails };
    const data = await updateVisitorInspectionReport(agencySlug, propertyId, payload);
    setReport(data);
    return data;
  };

  const handleGenerate = async () => {
    if (!agencySlug || !propertyId) return;
    try {
      setSaving(true);
      setError(null);
      const data = await generateVisitorInspectionReport(agencySlug, propertyId, { periodStart, periodEnd });
      setReport(data);
      setSummary(data.summary || '');
      setAgentNotes(data.agentNotes || '');
      setIncludedAttendeeIds(data.includedAttendeeIds || []);
      setPeriodStart(data.periodStart);
      setPeriodEnd(data.periodEnd);
      setVendorVisibleContactDetails(data.vendorVisibleContactDetails);
      setVendorEmails((data.vendorEmails || []).join(', '));
      lastFetchedPeriodRef.current = `${data.periodStart}|${data.periodEnd}`;
      setSuccess('Visitor inspection report generated.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to generate report');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await persistDraft();
      setSuccess('Report saved.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!agencySlug || !propertyId) return;
    try {
      setSaving(true);
      setError(null);
      const data = await finalizeVisitorInspectionReport(agencySlug, propertyId, {
        summary,
        agentNotes,
        includedAttendeeIds,
        periodStart,
        periodEnd,
        vendorVisibleContactDetails,
      });
      setReport(data);
      setSuccess('Report finalized and ready to share.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to finalize report');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!agencySlug || !propertyId) return;
    if (ownerEmailList.length === 0) {
      setError('Enter at least one vendor email address.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const result = await sendVisitorInspectionReportEmail(agencySlug, propertyId, ownerEmailList);
      setSuccess(`${result.message}. Access code: ${result.vendorAccessCode}`);
      await loadReport();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to send report');
    } finally {
      setSaving(false);
    }
  };

  const toggleAttendee = (attendeeId?: string) => {
    if (!attendeeId) return;
    setIncludedAttendeeIds(prev =>
      prev.includes(attendeeId) ? prev.filter(id => id !== attendeeId) : [...prev, attendeeId]
    );
  };

  const handleWeekShift = (direction: -1 | 1) => {
    if (!hasValidPeriod) return;
    const nextPeriod = shiftReportPeriod(periodStart, periodEnd, direction);
    setPeriodStart(nextPeriod.periodStart);
    setPeriodEnd(nextPeriod.periodEnd);
  };

  const handleSuggestSummary = async () => {
    if (!agencySlug || !propertyId || !hasValidPeriod) return;
    if (summary.trim().length > 40) {
      const ok = window.confirm('Replace your current Agent Summary with an AI-generated suggestion?');
      if (!ok) return;
    }
    try {
      setSuggestingSummary(true);
      setError(null);
      const { suggestedSummary } = await suggestVisitorInspectionReportSummary(agencySlug, propertyId, {
        periodStart,
        periodEnd,
      });
      setSummary(suggestedSummary);
      setSuccess('Suggested summary inserted. Review and edit before finalising.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to generate summary suggestion');
    } finally {
      setSuggestingSummary(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  if (!report) {
    return <Alert severity="error">{error || 'Report not found'}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(`/agencies/${agencySlug}/properties/${propertyId}`)} sx={{ mb: 2 }}>
        Back to Property
      </Button>

      <Box display="flex" justifyContent="space-between" gap={2} alignItems="flex-start" flexWrap="wrap" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>Visitor Inspection Report</Typography>
          <Typography color="text.secondary">{report.propertyAddress}</Typography>
        </Box>
        <Chip label={report.status} color={report.status === 'finalized' ? 'success' : 'default'} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {!hasValidPeriod && <Alert severity="warning" sx={{ mb: 2 }}>A valid report time period is required.</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2.4}><Card><CardContent><Typography variant="h5">{report.metrics.totalVisitors}</Typography><Typography color="text.secondary">Visitors</Typography></CardContent></Card></Grid>
        <Grid item xs={12} md={2.4}><Card><CardContent><Typography variant="h5">{report.metrics.feedbackCount}</Typography><Typography color="text.secondary">Feedback</Typography></CardContent></Card></Grid>
        <Grid item xs={12} md={2.4}><Card><CardContent><Typography variant="h5">{report.metrics.highInterestCount}</Typography><Typography color="text.secondary">High Interest</Typography></CardContent></Card></Grid>
        <Grid item xs={12} md={2.4}><Card><CardContent><Typography variant="h5">{report.metrics.contractRequestCount}</Typography><Typography color="text.secondary">Contract requests</Typography></CardContent></Card></Grid>
        <Grid item xs={12} md={2.4}><Card><CardContent><Typography variant="h5">{report.metrics.sessionCount}</Typography><Typography color="text.secondary">Sessions</Typography></CardContent></Card></Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            <Button startIcon={<AutoAwesome />} variant="contained" onClick={handleGenerate} disabled={saving || !hasValidPeriod}>
              Generate
            </Button>
            <Button startIcon={<Save />} variant="outlined" onClick={handleSave} disabled={saving || !hasValidPeriod}>
              Save Draft
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Report Period Start"
                type="date"
                value={periodStart}
                onChange={(event) => setPeriodStart(event.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Report Period End"
                type="date"
                value={periodEnd}
                onChange={(event) => setPeriodEnd(event.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button variant="outlined" onClick={() => handleWeekShift(-1)} disabled={!hasValidPeriod || saving || periodRefreshing}>
                  Previous Week
                </Button>
                <Button variant="outlined" onClick={() => handleWeekShift(1)} disabled={!hasValidPeriod || saving || periodRefreshing}>
                  Next Week
                </Button>
                {periodRefreshing && (
                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Updating report for selected period…
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={suggestingSummary ? <CircularProgress color="inherit" size={18} /> : <AutoAwesome />}
                onClick={handleSuggestSummary}
                disabled={saving || suggestingSummary || !hasValidPeriod}
              >
                Suggest summary
              </Button>
            </Box>
            <TextField
              label="Agent Summary"
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
              fullWidth
              multiline
              minRows={3}
              helperText="Optional: AI drafts from anonymised inspection data for this period only. Always review before sharing with the vendor."
            />
          </Box>
          <TextField
            label="Internal Agent Notes"
            helperText="Not shown to the vendor."
            value={agentNotes}
            onChange={(event) => setAgentNotes(event.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={vendorVisibleContactDetails} onChange={(event) => setVendorVisibleContactDetails(event.target.checked)} />}
            label="Show visitor email and phone details to vendor"
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Visitor Feedback Included in Vendor Report</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Include</TableCell>
                  <TableCell>Visitor</TableCell>
                  <TableCell>Visit</TableCell>
                  <TableCell>Interest</TableCell>
                  <TableCell>Contract</TableCell>
                  <TableCell>Feedback</TableCell>
                  <TableCell>Price / Objections</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.attendees.map(attendee => (
                  <TableRow key={attendee._id || attendee.name}>
                    <TableCell>
                      <Checkbox checked={includedAttendeeIds.includes(attendee._id || '')} onChange={() => toggleAttendee(attendee._id)} />
                    </TableCell>
                    <TableCell>{attendee.name}</TableCell>
                    <TableCell>{formatDate(attendee.visitDate)} {attendee.visitType ? `(${attendee.visitType.replace('_', ' ')})` : ''}</TableCell>
                    <TableCell>{attendee.interestLevel || (attendee.keenBuyer ? 'keen buyer' : '-')}</TableCell>
                    <TableCell>
                      {attendeeRequestedContract(attendee) ? (
                        <Chip size="small" label="Requested" color="primary" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{attendee.feedback || '-'}</TableCell>
                    <TableCell>
                      {attendee.priceFeedback && <Typography variant="body2">Price: {attendee.priceFeedback}</Typography>}
                      {attendee.objections && <Typography variant="body2">Objections: {attendee.objections}</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Share with Vendor</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Finalize the report before sending. Use comma-separated emails for multiple vendors.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Vendor email addresses"
              value={vendorEmails}
              onChange={(event) => setVendorEmails(event.target.value)}
              sx={{ minWidth: 320, flex: 1 }}
            />
            <Button startIcon={<CheckCircle />} variant="outlined" color="success" onClick={handleFinalize} disabled={saving || !hasValidPeriod}>
              Finalize
            </Button>
            <Button startIcon={<Email />} variant="contained" onClick={handleSend} disabled={saving || report.status !== 'finalized' || !hasValidPeriod}>
              Send Report
            </Button>
          </Box>
          {report.vendorAccessCode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Current vendor access code: {report.vendorAccessCode}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VisitorInspectionReportEdit;
